import * as R from 'ramda';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { ExtendedCluster } from './solana';

export type PassType = 'ignite' | 'blueNile' | 'uniqueness';

type PortalConfigs = {
  gatewayStage: string;
  network: ExtendedCluster;
  passConfigs: Record<PassType, PassConfig>;
  walletAdapterNetwork: WalletAdapterNetwork;
};

type PassConfig = {
  gatekeeperNetworkPublicKey: string;
  useClientSends: boolean;
};

const portalConfigs: Record<string, PortalConfigs> = {
  e2e: {
    gatewayStage: 'dev',
    network: 'devnet',
    passConfigs: {
      ignite: {
        gatekeeperNetworkPublicKey: 'tigoYhp9SpCDoCQmXGj2im5xa3mnjR1zuXrpCJ5ZRmi',
        useClientSends: true,
      },
      blueNile: {
        gatekeeperNetworkPublicKey: 'tbniJdS9j7BWhUoJesjpqutC54AYr96cn2No7dJcqce',
        useClientSends: true,
      },
      uniqueness: {
        gatekeeperNetworkPublicKey: 'tunQheuPpHhjjsbrUDp4rikqYez9UXv4SXLRHf9Kzsv',
        useClientSends: false,
      },
    },
    walletAdapterNetwork: WalletAdapterNetwork.Devnet,
  },
  dev: {
    gatewayStage: 'dev',
    network: 'devnet',
    passConfigs: {
      ignite: {
        gatekeeperNetworkPublicKey: 'tigoYhp9SpCDoCQmXGj2im5xa3mnjR1zuXrpCJ5ZRmi',
        useClientSends: true,
      },
      blueNile: {
        gatekeeperNetworkPublicKey: 'tbniJdS9j7BWhUoJesjpqutC54AYr96cn2No7dJcqce',
        useClientSends: true,
      },
      uniqueness: {
        gatekeeperNetworkPublicKey: 'tunQheuPpHhjjsbrUDp4rikqYez9UXv4SXLRHf9Kzsv',
        useClientSends: true,
      },
    },
    walletAdapterNetwork: WalletAdapterNetwork.Devnet,
  },
  preprod: {
    gatewayStage: 'preprod',
    network: 'devnet',
    passConfigs: {
      ignite: {
        gatekeeperNetworkPublicKey: 'tigoYhp9SpCDoCQmXGj2im5xa3mnjR1zuXrpCJ5ZRmi',
        useClientSends: true,
      },
      blueNile: {
        gatekeeperNetworkPublicKey: 'tbniJdS9j7BWhUoJesjpqutC54AYr96cn2No7dJcqce',
        useClientSends: true,
      },
      uniqueness: {
        gatekeeperNetworkPublicKey: 'tunQheuPpHhjjsbrUDp4rikqYez9UXv4SXLRHf9Kzsv',
        useClientSends: true,
      },
    },
    walletAdapterNetwork: WalletAdapterNetwork.Devnet,
  },
  prod: {
    gatewayStage: 'prod',
    network: 'mainnet-beta',
    passConfigs: {
      ignite: {
        gatekeeperNetworkPublicKey: 'ignREusXmGrscGNUesoU9mxfds9AiYTezUKex2PsZV6',
        useClientSends: true,
      },
      blueNile: {
        gatekeeperNetworkPublicKey: 'bni1ewus6aMxTxBi5SAfzEmmXLf8KcVFRmTfproJuKw',
        useClientSends: true,
      },
      uniqueness: {
        gatekeeperNetworkPublicKey: 'uniqobk8oGh4XBLMqM68K8M2zNu3CdYX7q5go7whQiv',
        useClientSends: true,
      },
    },
    walletAdapterNetwork: WalletAdapterNetwork.Mainnet,
  },
  'prod-devnet': {
    gatewayStage: 'prod',
    network: 'devnet',
    passConfigs: {
      ignite: {
        gatekeeperNetworkPublicKey: 'ignREusXmGrscGNUesoU9mxfds9AiYTezUKex2PsZV6',
        useClientSends: true,
      },
      blueNile: {
        gatekeeperNetworkPublicKey: 'bni1ewus6aMxTxBi5SAfzEmmXLf8KcVFRmTfproJuKw',
        useClientSends: true,
      },
      uniqueness: {
        gatekeeperNetworkPublicKey: 'uniqobk8oGh4XBLMqM68K8M2zNu3CdYX7q5go7whQiv',
        useClientSends: true,
      },
    },
    walletAdapterNetwork: WalletAdapterNetwork.Devnet,
  },
};

export type PortalConfig = Omit<PortalConfigs, 'passConfigs'> &
  PassConfig & {
    i18nContext: PassType;
  };

function getPortalConfig(): PortalConfig {
  const stage = process.env.REACT_APP_STAGE || 'dev';
  const passType = (process.env.REACT_APP_PASS_TYPE as PassType) || 'ignite';
  const config = R.propOr<PortalConfigs, Record<string, PortalConfigs>, PortalConfigs>(
    portalConfigs.dev,
    stage,
    portalConfigs
  );
  const partialConfig = R.dissoc('passConfigs', config);
  return {
    ...partialConfig,
    gatekeeperNetworkPublicKey: config.passConfigs[passType].gatekeeperNetworkPublicKey,
    useClientSends: config.passConfigs[passType].useClientSends,
    i18nContext: passType,
  };
}

export { getPortalConfig };
